// FireworksEffect.js
import React, { useRef, useEffect } from "react";

const COLORS = [
  "#ff1744",
  "#ff9800",
  "#ffd600",
  "#00e676",
  "#40c4ff",
  "#7c4dff",
  "#ffffff",
  "#ff4081",
];

export default function FireworksEffect({
  style = {},
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let width = 0;
    let height = 0;
    let animation;

    const fireworks = [];
    const particles = [];

    const resize = () => {
      width = canvas.parentElement
        ? canvas.parentElement.clientWidth
        : window.innerWidth;

      height = canvas.parentElement
        ? canvas.parentElement.clientHeight
        : window.innerHeight;

      canvas.width = width;
      canvas.height = height;
    };

    resize();
    window.addEventListener("resize", resize);

    class Firework {
      constructor() {
        this.x = Math.random() * width;
        this.y = height;
        this.targetY = 80 + Math.random() * (height * 0.45);
        this.speed = 5 + Math.random() * 3;
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
      }

      update() {
        this.y -= this.speed;

        ctx.beginPath();
        ctx.arc(this.x, this.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();

        if (this.y <= this.targetY) {
          explode(this.x, this.y, this.color);
          return true;
        }

        return false;
      }
    }

    class Particle {
      constructor(x, y, color) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 6 + 2;

        this.x = x;
        this.y = y;

        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;

        this.alpha = 1;
        this.life = 60 + Math.random() * 20;
        this.color = color;
        this.size = Math.random() * 1 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        this.vx *= 0.98;
        this.vy *= 0.98;
        this.vy += 0.05;

        this.life--;
        this.alpha = this.life / 80;

        ctx.globalAlpha = Math.max(this.alpha, 0);

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();

        ctx.globalAlpha = 1;

        return this.life <= 0;
      }
    }

    function explode(x, y, color) {
      const count = 80 + Math.random() * 40;

      for (let i = 0; i < count; i++) {
        particles.push(new Particle(x, y, color));
      }
    }

    function launch() {
      fireworks.push(new Firework());

      setTimeout(() => {
        launch();
      }, 500 + Math.random() * 900);
    }

    launch();

    function animate() {
      animation = requestAnimationFrame(animate);

      ctx.fillStyle = "rgba(0,0,0,0.15)";
      ctx.fillRect(0, 0, width, height);

      for (let i = fireworks.length - 1; i >= 0; i--) {
        if (fireworks[i].update()) {
          fireworks.splice(i, 1);
        }
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        if (particles[i].update()) {
          particles.splice(i, 1);
        }
      }
    }

    animate();

    return () => {
      cancelAnimationFrame(animation);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 2,
        ...style,
      }}
    />
  );
}